/**
 * action.status payload builders (Phase-2b narration-sync, FE emission).
 *
 * The copilot's voice must line up with what actually happened on screen, so
 * the FE reports an execution-status LIFECYCLE for each action:
 *   - `completed` — the action actually succeeded (the agent then confirms in
 *                   past tense: "Opened the dashboard");
 *   - `failed`    — it errored / timed out.
 *
 * Confirm-only narration: we do NOT emit a `started` phase (a pre-cue on every
 * action was too chatty). These are PURE builders so the mapping is
 * unit-testable without a socket or a rendered component. The panel calls them
 * around invokeCopilotAction and ships the result via
 * controlSocket.sendActionStatus.
 *
 * A `blocked` result (needs_confirmation, validation_failed, a precondition)
 * is deliberately NOT a lifecycle event: the action did not run, but it isn't
 * a hard failure either — it's mid-flow, and the goal/confirmation path
 * narrates it. resultStatus() returns null for it so we never mis-voice a
 * confirmation prompt as "that failed".
 */

const AGENT = "agent";

const base = (dispatch, phase) => ({
  origin: AGENT,
  phase,
  action: dispatch?.tool_name ?? "",
  callId: dispatch?.call_id ?? null,
  node: null,
  errorCode: null,
});

/**
 * Emitted when invokeCopilotAction resolves. Maps the result status to a
 * lifecycle phase; returns null for `blocked` (mid-flow, not a completion).
 * `node` (the resulting route-map node) is carried through from the result
 * data when the handler provides it.
 */
export function resultStatus(dispatch, result) {
  const status = result?.status;
  if (status === "ok") {
    return {
      ...base(dispatch, "completed"),
      node: result?.data?.node ?? null,
    };
  }
  if (status === "error") {
    return {
      ...base(dispatch, "failed"),
      errorCode: result?.errorCode ?? result?.error_code ?? null,
    };
  }
  // status === "blocked" (or anything unexpected) → no lifecycle event.
  return null;
}

/** Emitted on a bridge exception / host timeout, where there's no result. */
export function failedStatus(dispatch, errorCode) {
  return { ...base(dispatch, "failed"), errorCode: errorCode ?? null };
}

/**
 * A MANUAL action the doctor performed themselves (currently: navigation).
 * origin="manual", no call_id, always the terminal `completed` phase with the
 * resulting route-map node. narration-sync stays silent on these unless they
 * diverge from an in-flight task.
 */
export function manualStatus({ action = "navigate", node = null } = {}) {
  return {
    origin: "manual",
    phase: "completed",
    action,
    callId: null,
    node,
    errorCode: null,
  };
}
